import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import './bookingPriceChanges.css';
import { SkeletonRows } from './SectionSkeleton';

const money = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm') : '—');

// The action taken on the money for this edit → { text, tone } or null.
const settlementLine = (h) => {
    const amt = money(h.settlement_amount);
    switch (h.settlement) {
        case 'refunded_to_wallet': return { text: `${amt} refunded to wallet`, tone: 'refund' };
        case 'charged_to_wallet': return { text: `${amt} charged from wallet`, tone: 'charge' };
        case 'charged_to_card': return { text: `${amt} charged to card`, tone: 'charge' };
        // "the invoice", not "next invoice" — an invoiced booking's edit lands
        // on ITS invoice immediately (2026-09-02); an uninvoiced one rides the
        // next. "The invoice" reads true for both.
        case 'added_to_invoice': return { text: `${amt} added to the invoice balance`, tone: 'pending' };
        case 'reduced': return { text: `${amt} less to invoice`, tone: 'refund' };
        default: return null;
    }
};

/**
 * PRICE CHANGES + PENDING for a booking (owner 2026-07-21 — replaces price
 * adjustments). Shows what changed on each edit (added/removed item, time,
 * duration, price) with the total before→after, plus the booking's outstanding
 * amount. Money is settled from the wallet when it covers, otherwise billed on
 * the invoice — never a separate adjustment. Admin view; the client sees the
 * same data on their booking page.
 */
export default function BookingPriceChanges({ bookingId, refreshTick = 0 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const res = await axios.get(`/api/bookings/admin/${bookingId}/price-changes`);
                if (alive) setData(res.data);
            } catch (e) {
                if (alive) setData(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [bookingId, refreshTick]);

    if (loading) {
        return (
            <section className="dp-card booking-section bpc">
                <h3 className="bpc__title">Price Changes</h3>
                <SkeletonRows rows={2} />
            </section>
        );
    }
    if (!data) return null;
    const history = Array.isArray(data.price_changes) ? data.price_changes : [];
    const pending = Number(data.pending || 0);
    // Nothing to show → render nothing (keeps the booking page clean).
    if (history.length === 0 && pending <= 0.009 && !data.invoice_paid) return null;

    return (
        <section className="dp-card booking-section bpc">
            <div className="bpc__head">
                <h3 className="bpc__title">Price Changes</h3>
                {/* The paid badge no longer needs a non-zero pending to appear.
                    It only ever required one because pending stayed stuck at the
                    booking total on an invoice-billed booking; now that a paid
                    invoice correctly clears it, gating on pending would hide the
                    badge on exactly the bookings it describes (2026-08-30). */}
                {data.invoice_paid ? (
                    <span className="bpc__pending bpc__pending--paid">
                        Paid{data.invoice_number ? ` · invoice ${data.invoice_number}` : ''}
                    </span>
                ) : pending > 0.009 ? (
                    <span className="bpc__pending">
                        Pending: {money(pending)} · {data.invoiced
                            ? `invoiced${data.invoice_number ? ` ${data.invoice_number}` : ''}`
                            : 'on next invoice'}
                    </span>
                ) : null}
            </div>

            {history.length === 0 ? (
                <div className="bpc__empty">No price changes recorded for this booking.</div>
            ) : (
                <ul className="bpc__list">
                    {history.map((h) => (
                        <li key={h.id} className="bpc__item">
                            <div className="bpc__itemTop">
                                <span className="bpc__lines">
                                    {(h.changes || []).length ? h.changes.join(' · ') : 'Price updated'}
                                </span>
                                {h.old_total != null && h.new_total != null && (
                                    <span className={`bpc__delta ${h.new_total >= h.old_total ? 'bpc__delta--up' : 'bpc__delta--down'}`}>
                                        {money(h.old_total)} → {money(h.new_total)}
                                    </span>
                                )}
                            </div>
                            {settlementLine(h) && (
                                <div className={`bpc__action bpc__action--${settlementLine(h).tone}`}>
                                    {settlementLine(h).text}
                                </div>
                            )}
                            <div className="bpc__meta">
                                {fmtDate(h.created_at)}{h.changed_by_name ? ` · by ${h.changed_by_name}` : ''}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
